// Main App component that renders all UI controls

import { Signal } from '@preact/signals';
import { Legend } from './Legend';
import { BoundaryControl } from './BoundaryControl';
import { IsochronePanel } from './IsochronePanel';
import type { ColorManager } from '../../utils/colors';
import type { IsochroneController } from '../isochrone-controller';

interface AppProps {
  // Legend props
  types: Signal<Set<string>>;
  colorManager: ColorManager;
  onColorChange?: (type: string, color: string) => void;

  // Boundary props
  regionsEnabled: Signal<boolean>;
  departmentsEnabled: Signal<boolean>;
  onRegionToggle: (enabled: boolean) => void;
  onDepartmentToggle: (enabled: boolean) => void;

  // Isochrone controller
  isochroneController: IsochroneController;

  // Filter callback
  onFilterChange: (checked: boolean) => void;
}

/**
 * Main App component rendering all UI controls
 */
export function App({
  types,
  colorManager,
  onColorChange,
  regionsEnabled,
  departmentsEnabled,
  onRegionToggle,
  onDepartmentToggle,
  isochroneController,
  onFilterChange,
}: AppProps) {
  return (
    <>
      <Legend types={types} colorManager={colorManager} onColorChange={onColorChange} />
      <BoundaryControl
        regionsEnabled={regionsEnabled}
        departmentsEnabled={departmentsEnabled}
        onRegionToggle={onRegionToggle}
        onDepartmentToggle={onDepartmentToggle}
      />
      <IsochronePanel
        config={isochroneController.config}
        lastPoint={isochroneController.lastPoint}
        loading={isochroneController.loading}
        hasResult={isochroneController.hasResult}
        resultLabel={isochroneController.resultLabel}
        hintText={isochroneController.hintText}
        filterMarkers={isochroneController.filterMarkers}
        onCalculate={async () => {
          await isochroneController.calculate();
          if (isochroneController.filterMarkers.value) {
            onFilterChange(true);
          }
        }}
        onClear={() => {
          isochroneController.clear();
          onFilterChange(false);
        }}
        onFilterChange={onFilterChange}
      />
    </>
  );
}
